/**
 * A turn that FAILED, as the chat shows it.
 *
 * When a turn dies (a gateway error, a streaming throw, an empty completion after the retry budget), the
 * harness persists one canned ASSISTANT row — "Sorry, I ran into an issue and couldn't finish…" — tagged
 * `metadata.systemError` (`persistTurnError` in orion-harness-poc's assistant-service). Until this module
 * no client read the tag, so the apology rendered as an ordinary reply: the reader could not tell the turn
 * had failed, and the only way to retry was to retype the message.
 *
 * Two pure pieces, mirrored 1:1 by iOS (`SymphonyAPI.systemError(from:)`, `FailedTurnRetry`) and Android
 * (`parseSystemError`, `failedTurnRetryText`):
 * - `readSystemErrorFlag` — the tag off a row's metadata. Tolerant of the `"true"` string form, because
 *   the wire metadata is a `map<string,string>` on some paths (same reason as `readMeetingSummaryFlag`),
 *   and of metadata that arrives as a JSON-encoded string — the same tolerance the phones' parsers have.
 * - `failedTurnRetryTarget` — which row offers "Try again", and the text it re-sends.
 *
 * No runtime imports, so the server (`/api/conversations/[id]/messages`), the client (`use-chat`) and a
 * unit test can all read it without dragging the app's dependency graph (see CLAUDE.md → Tests).
 */

export function readSystemErrorFlag(metadata: unknown): boolean {
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return false;
    }
  }
  if (!metadata || typeof metadata !== 'object') return false;
  const value = (metadata as { systemError?: unknown }).systemError;
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

/** The fields this module reads — a structural subset of `use-chat`'s `Message`. */
export type FailedTurnRow = {
  id?: string;
  role?: string;
  content?: string;
  attachments?: readonly unknown[];
  systemError?: boolean;
};

export type FailedTurnRetry = { rowId: string; text: string };

/**
 * The failed-turn row that offers "Try again", and the text it re-sends — or null when none should.
 *
 * Only the NEWEST failed-turn row can offer it, and only while nothing has been said since: a USER row
 * after it means the reader already moved on (or already retried), and re-sending an older question
 * then would answer something nobody is waiting on. The text is that of the nearest USER row above the
 * failure — the message the failed turn was answering. A row with attachments is not retried: the
 * button re-sends TEXT, and re-sending only the caption of a message with files would be a different
 * message. A blank one has nothing to send.
 */
export function failedTurnRetryTarget(messages: readonly FailedTurnRow[]): FailedTurnRetry | null {
  let failedIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const row = messages[i];
    if (row.systemError) {
      failedIndex = i;
      break;
    }
    // Something was said AFTER the newest failure — no retry anywhere.
    if (row.role === 'USER') return null;
  }
  if (failedIndex < 0) return null;
  const rowId = messages[failedIndex].id;
  if (!rowId) return null;
  for (let i = failedIndex - 1; i >= 0; i--) {
    const row = messages[i];
    if (row.role !== 'USER') continue;
    if (row.attachments && row.attachments.length > 0) return null;
    const text = (row.content ?? '').trim();
    return text ? { rowId, text } : null;
  }
  return null;
}
